import NoRecords from '../../shared/NoRecords';
import React from 'react';
import MetricTile from '../MetricTile';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import { titleCase } from '../../shared/format';
import { fmtCount, fmtRatioPct, fmtSharePct, fmtPosition } from '../lib/format';

/** Export columns — raw payload values, not the rendered strings (see `lib/csv.js`). */
const CSV_COLUMNS = [
  { header: 'Device', get: (r) => titleCase(r.device) },
  { header: 'Visits', get: (r) => r.clicks },
  { header: 'Share of visits', get: (r) => r.click_share },
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: 'Click rate', get: (r) => r.ctr },
  { header: 'Position', get: (r) => r.position },
];

/**
 * Decorative accent + corner icon per device, so the three figures read as the same
 * KPI-tile family the image_search panel uses (`SurfaceStat`) rather than plain boxes.
 * Colour and icon carry no judgement here — they only separate the three tiles (§C.2 /
 * MetricTile). A device Google does not report falls back to the primary treatment.
 */
const DEVICE_STYLE = {
  MOBILE: { variant: 'primary', icon: 'phone' },
  DESKTOP: { variant: 'success', icon: 'chart' },
  TABLET: { variant: 'subtle', icon: 'target' },
};

/**
 * Evidence for `device_split`.
 *
 * `facts`: { items: [{ device, clicks, impressions, ctr, position, click_share, drill }],
 *            summary: { total_clicks, total_impressions }, pagination }
 *
 * ── A sentence and three inline figures, no chart (V6 §E.12) ─────────────────────
 * "Three values that barely move do not earn a chart." V6 drops the stacked bar the v2
 * build carried: it was the fourth bar-family form on the page (§4.9), and it encoded
 * three numbers a sentence already states. The collapsed view is now three inline
 * figures — one per device — each naming the device and its published share of visits.
 * The per-device metrics (CTR, position, raw counts) stay behind the expansion.
 *
 * ── Clicking a device (§E.12) ────────────────────────────────────────────────────
 * Each row carries its own `drill` (`gsc.device`). A figure with a drill is a button
 * that calls `onDrill(row.drill)` → `fn_drill` → `DrillOver`. The pre-V6 captures carry
 * no drill, so those figures render static.
 *
 * `click_share` is rendered as it ARRIVES — never recomputed as `clicks / total_clicks`,
 * which would be a second, silently different denominator (§8). `device` is Google's
 * enum (`MOBILE` / `DESKTOP` / `TABLET`), title-cased for display.
 *
 * Note: `facts.pagination` carries `total_items: 3` — an artefact of the shared list
 * builder. There are always exactly three devices and never a second page; it is ignored.
 */

const DeviceFigure = ({ row, onDrill }) => {
  const drillable = Boolean(row?.drill) && typeof onDrill === 'function';
  const style = DEVICE_STYLE[row.device] || DEVICE_STYLE.MOBILE;
  return (
    <MetricTile
      data-device={row.device}
      label={titleCase(row.device)}
      value={fmtSharePct(row.click_share)}
      subLabel={row.clicks != null ? `${fmtCount(row.clicks)} visits` : undefined}
      variant={style.variant}
      icon={style.icon}
      onClick={drillable ? () => onDrill(row.drill) : undefined}
    />
  );
};

const DeviceSplit = ({ facts, sentence, collapsed = true, onDrill }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];

  const columns = [
    { key: 'device', label: 'Device', render: (r) => titleCase(r.device) },
    { key: 'clicks', label: 'Visits', align: 'right', render: (r) => fmtCount(r.clicks) },
    { key: 'click_share', label: 'Share of visits', align: 'right', render: (r) => fmtSharePct(r.click_share) },
    { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
    { key: 'ctr', label: 'Click rate', align: 'right', render: (r) => fmtRatioPct(r.ctr) },
    { key: 'position', label: 'Position', align: 'right', render: (r) => fmtPosition(r.position) },
  ];

  if (items.length === 0) {
    return <NoRecords />;
  }

  return (
    <div className="space-y-3">
      {/* Three inline figures, one per device (§E.12) — no chart. */}
      <div
        data-testid="device-figures"
        aria-label={sentence || 'Share of visits by device'}
        className="grid grid-cols-3 gap-3"
      >
        {items.map((r) => (
          <DeviceFigure key={r.device} row={r} onDrill={onDrill} />
        ))}
      </div>

      {/* ── Expanded only: per-device metrics (§7) ───────────────────────────── */}
      {!collapsed && (
        <div className="border-t border-gray-200 dark:border-gray-800 pt-3">
          <PaginatedTable
            columns={columns}
            rows={items}
            rowKey={(r) => r.device}
            label="devices"
            ariaLabel={sentence}
          />
          <CsvButton panelKey="device_split" name="Phone versus computer" columns={CSV_COLUMNS} rows={items} />
        </div>
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * The bar shows share of visits only. Expanding adds click rate, position and the raw
 * impression counts per device — figures the bar cannot carry.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
DeviceSplit.hasDepth = (facts) => Array.isArray(facts?.items) && facts.items.length > 0;


export default DeviceSplit;
